/*
 * Command history — audit-view of every write action across the caller's
 * accessible data scopes. Backed by GET /api/commands/history
 * (ListCommandHistoryEndpoint.cs, capped at 1000 rows).
 *
 * Migrated to the TanStack-Table wrapper (DataTable) in issue #13: sortable
 * columns on top of the previous read-only view. Sort is client-side over
 * the already-capped 1000-row window; going deeper is still an audit-backend
 * job.
 *
 * Plant-scoped variant: when opened via /plants/:id/commands the plantId is
 * forwarded to the API as a filter and the PlantSubNav is shown. Global
 * variant at /commands/history stays unchanged.
 *
 * In der Anlage stehen hier auch die Schreibungen im OEM-Modus (Servicezugang
 * am Panel oder aus der Ferne, Schreibprotokoll), gekennzeichnet mit ihrer
 * Freigabe (Patrick, 22.10.2026: das Protokoll gehoert in die Befehls-Historie,
 * nicht in das Panel-Fenster). Ohne Recht auf das Protokoll fehlen sie still.
 */

import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { PlantSubNav } from '../components/PlantSubNav';
import { DataTable } from '../components/ui/DataTable';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useAsync } from '../hooks/useAsync';
import { api } from '../api/client';
import type {
  CommandHistoryEntry,
  CommandStatus,
  ListCommandHistoryResponse,
  ListPlantsResponse,
} from '../api/types';
import { protocolToHistoryRow, type HistoryRow, type ServiceProtocolRow } from '../components/serviceRemoteModel';

interface Props { id?: string }

export function CommandHistoryPage({ id: plantIdFromRoute }: Props = {}) {
  const lockedToPlant = !!plantIdFromRoute;

  const state = useAsync(
    (signal) => {
      const url = lockedToPlant
        ? `/api/commands/history?plantId=${plantIdFromRoute}`
        : '/api/commands/history';
      return api.get<ListCommandHistoryResponse>(url, signal);
    },
    [plantIdFromRoute],
  );

  const protocol = useAsync(
    (signal) => (lockedToPlant
      ? api.get<ServiceProtocolRow[]>(`/api/plants/${plantIdFromRoute}/service-protocol?limit=1000`, signal).catch(() => [])
      : Promise.resolve([] as ServiceProtocolRow[])),
    [plantIdFromRoute],
  );

  const rows = useMemo<HistoryRow[]>(() => {
    if (state.status !== 'ok') return [];
    const commands = state.data.history.map(fromCommand);
    const oem = protocol.status === 'ok' ? protocol.data.map(protocolToHistoryRow) : [];
    return [...commands, ...oem];
  }, [state, protocol]);

  // Only needed to resolve the plant display name for the sub-nav header.
  const plantsState = useAsync(
    (signal) => api.get<ListPlantsResponse>('/api/plants', signal),
    [],
  );
  const plantName = useMemo(() => {
    if (!plantIdFromRoute || plantsState.status !== 'ok') return '';
    return plantsState.data.plants.find((p) => p.id === plantIdFromRoute)?.displayName ?? '';
  }, [plantIdFromRoute, plantsState]);

  const columns: ColumnDef<HistoryRow, unknown>[] = [
    {
      id: 'at',
      accessorKey: 'at',
      header: 'Zeitpunkt',
      cell: ({ row }) => <span class="mono">{formatTimestamp(row.original.at)}</span>,
      sortingFn: 'datetime',
    },
    {
      id: 'mode',
      accessorFn: (r) => r.oem ?? '',
      header: 'Modus',
      cell: ({ row }) => (row.original.oem
        ? <StatusBadge tone="warn" title={`OEM-Modus, Freigabe: ${row.original.oem}`}>OEM · {row.original.oem}</StatusBadge>
        : <span class="muted">Betrieb</span>),
    },
    {
      id: 'action',
      accessorKey: 'action',
      header: 'Befehl',
      cell: ({ row }) => <span class="mono">{row.original.action}</span>,
    },
    {
      id: 'target',
      accessorKey: 'target',
      header: 'Datenpunkt',
      cell: ({ row }) => (
        <span class={row.original.oem ? '' : 'mono muted'} title={row.original.targetTitle}>{row.original.target}</span>
      ),
    },
    {
      id: 'value',
      accessorKey: 'value',
      header: 'Wert',
      cell: ({ row }) => <span class="mono" title={row.original.value}>{row.original.value}</span>,
      enableSorting: false,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusBadge tone={row.original.tone} title={row.original.statusTitle ?? undefined}>
          {row.original.status}
        </StatusBadge>
      ),
    },
  ];

  return (
    <AppShell title={lockedToPlant ? 'Befehle' : 'Command History (alle Anlagen)'}>
      {lockedToPlant && <PlantSubNav plantId={plantIdFromRoute!} plantName={plantName} />}
      {state.status === 'loading' && <p class="muted">Loading…</p>}
      {state.status === 'error' && <p class="muted">Failed to load command history.</p>}
      {state.status === 'ok' && (
        <DataTable<HistoryRow>
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          initialSorting={[{ id: 'at', desc: true }]}
          emptyLabel="Noch keine Schreibaktionen im sichtbaren Zeitraum."
        />
      )}
    </AppShell>
  );
}

function fromCommand(c: CommandHistoryEntry): HistoryRow {
  return {
    id: c.id,
    at: c.submittedAt,
    action: c.commandName,
    target: shortGuid(c.leafId),
    targetTitle: c.leafId,
    value: c.payloadJson,
    status: c.status,
    tone: toneForStatus(c.status) === 'accent' ? 'info' : toneForStatus(c.status) as HistoryRow['tone'],
    statusTitle: c.errorMessage,
    oem: null,
  };
}

function toneForStatus(status: CommandStatus): 'accent' | 'ok' | 'warn' | 'error' | 'info' | 'neutral' {
  switch (status) {
    case 'Sent':     return 'info';
    case 'Ack':      return 'info';
    case 'Executed': return 'ok';
    case 'Failed':   return 'error';
    case 'Timeout':  return 'warn';
    default:         return 'neutral';
  }
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function shortGuid(g: string): string {
  return g.length > 8 ? g.slice(0, 8) + '…' : g;
}
